import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Calendar, DollarSign, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPayments = () => {
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [patientTreatments, setPatientTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    patient_id: '', treatment_id: '', amount: '', status: 'paid', date: new Date().toISOString().split('T')[0], invoice_number: '', description: ''
  });

  useEffect(() => { fetchPayments(); fetchPatients(); fetchTreatments(); }, []);

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      setPayments(res.data);
    } catch (e) { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  };

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/admin/patients`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      setPatients(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchTreatments = async () => {
    try {
      const res = await axios.get(`${API}/admin/treatments`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      setTreatments(res.data);
    } catch (e) { console.error(e); }
  };

  const generateInvoiceNumber = () => `INV-${Date.now().toString().slice(-8)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const payload = { ...formData, amount: parseFloat(formData.amount), invoice_number: formData.invoice_number || generateInvoiceNumber(), treatment_id: formData.treatment_id || null };
      if (editingPayment) {
        await axios.put(`${API}/admin/payments/${editingPayment.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Payment updated');
      } else {
        await axios.post(`${API}/admin/payments`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Payment added');
      }
      closeForm();
      fetchPayments();
    } catch (e) { toast.error('Failed to save payment'); }
  };

  const handleEdit = (p) => {
    setEditingPayment(p);
    const pts = treatments.filter(t => t.patient_id === p.patient_id);
    setPatientTreatments(pts);
    setFormData({ patient_id: p.patient_id, treatment_id: p.treatment_id || '', amount: p.amount.toString(), status: p.status, date: p.date, invoice_number: p.invoice_number, description: p.description });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/admin/payments/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      toast.success('Deleted');
      setPayments(payments.filter(p => p.id !== id));
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API}/admin/payments/${id}`, { status }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      toast.success('Status updated');
      fetchPayments();
    } catch (e) { toast.error('Failed to update'); }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPayment(null);
    setFormData({ patient_id: '', treatment_id: '', amount: '', status: 'paid', date: new Date().toISOString().split('T')[0], invoice_number: '', description: '' });
    setPatientTreatments([]);
  };

  const openForm = () => { closeForm(); setShowForm(true); setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); };

  const getStatusBadge = (s) => ({ paid: 'bg-green-100 text-green-700', pending: 'bg-orange-100 text-orange-700', overdue: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-700');

  const filtered = payments.filter(p =>
    (p.patient_name && p.patient_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    p.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-payments-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.paymentManagement}</h1>
          <p className="text-[#6B7280]">{t.managePayments}</p>
        </div>
        <button onClick={openForm} className="bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold inline-flex items-center transition-all shadow-lg" data-testid="add-payment-button">
          <Plus className="w-5 h-5 mr-2" /> {t.addPayment}
        </button>
      </div>

      {/* INLINE FORM */}
      {showForm && (
        <div ref={formRef} className="bg-white rounded-xl border-2 border-[#0F4C5C]/20 shadow-lg p-6 mb-8" data-testid="payment-form">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: 'Playfair Display, serif' }}>{editingPayment ? t.editPayment : t.addNewPayment}</h3>
            <button onClick={closeForm} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-form"><X className="w-5 h-5 text-[#6B7280]" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.patient}</label>
              <select value={formData.patient_id} onChange={e => { const pid = e.target.value; setPatientTreatments(treatments.filter(t => t.patient_id === pid)); setFormData({ ...formData, patient_id: pid, treatment_id: '' }); }} required disabled={!!editingPayment} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="payment-patient-select">
                <option value="">{t.selectPatient}</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} - {p.email}</option>)}
              </select>
            </div>
            {formData.patient_id && (
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.forTreatment}</label>
                <select value={formData.treatment_id} onChange={e => { const tid = e.target.value; const sel = patientTreatments.find(t => t.id === tid); setFormData({ ...formData, treatment_id: tid, description: sel ? `${sel.treatment_type} - ${sel.doctor} (${new Date(sel.date).toLocaleDateString()})` : formData.description }); }} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="payment-treatment-select">
                  <option value="">{t.selectTreatmentOptional}</option>
                  {patientTreatments.map(t => <option key={t.id} value={t.id}>{t.treatment_type} - {t.doctor} ({new Date(t.date).toLocaleDateString()}) - ${t.cost.toFixed(2)}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.date}</label>
                <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.amount} ($)</label>
                <input type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.invoiceNumber}</label>
                <input type="text" value={formData.invoice_number} onChange={e => setFormData({ ...formData, invoice_number: e.target.value })} placeholder={t.autoGenerated} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.status}</label>
                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]">
                  <option value="paid">{t.paid}</option>
                  <option value="pending">{t.pending}</option>
                  <option value="overdue">{t.overdue}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.description}</label>
              <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows="3" placeholder="Payment details..." className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
            </div>
            <div className="flex space-x-4 pt-2">
              <button type="button" onClick={closeForm} className="flex-1 px-6 py-3 border-2 border-[#0F4C5C] text-[#0F4C5C] rounded-full font-semibold hover:bg-[#0F4C5C]/5 transition-colors">{t.cancel}</button>
              <button type="submit" className="flex-1 px-6 py-3 bg-[#0F4C5C] text-white rounded-full font-semibold hover:bg-[#0F4C5C]/90 transition-colors">{editingPayment ? t.updatePayment : t.addPayment}</button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input type="text" placeholder={t.search} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center"><p className="text-[#6B7280]">{searchTerm ? t.noResults : t.noPaymentsYet}</p></div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F9FAFB] border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.invoiceNum}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.patient}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.date}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.description}</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">{t.amount}</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">{t.status}</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">{t.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(p => (
                  <tr key={p.id} className="hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-4"><span className="font-mono text-sm">{p.invoice_number}</span></td>
                    <td className="px-6 py-4"><div className="font-medium text-[#1F2937]">{p.patient_name || 'Unknown'}</div></td>
                    <td className="px-6 py-4"><div className="flex items-center text-sm text-[#6B7280]"><Calendar className="w-4 h-4 mr-2" />{new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div></td>
                    <td className="px-6 py-4"><span className="text-sm text-[#6B7280]">{p.description}</span></td>
                    <td className="px-6 py-4 text-right"><span className="text-sm font-semibold">${p.amount.toFixed(2)}</span></td>
                    <td className="px-6 py-4 text-center">
                      <select value={p.status} onChange={e => handleStatusChange(p.id, e.target.value)} className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(p.status)} border-0 cursor-pointer`}>
                        <option value="paid">{t.paid}</option>
                        <option value="pending">{t.pending}</option>
                        <option value="overdue">{t.overdue}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => handleEdit(p)} className="p-2 text-[#0F4C5C] hover:bg-[#0F4C5C]/10 rounded-lg"><Edit className="w-5 h-5" /></button>
                        <button onClick={() => handleDelete(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-5 h-5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;
